import { ActionPerformed, PushNotifications, PushNotificationSchema } from "@capacitor/push-notifications";
import { useIonToast } from "@ionic/react";
import { useEffect, useMemo, useRef } from "react";

import useMe from "@hooks/useMe";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import {
  PushNotificationPlatformEnum,
  PushNotificationTokenCreateDocument,
  PushNotificationTokenCreateMutation,
  PushNotificationTokenCreateMutationVariables
} from "@typing/Generated";
import { IconList } from "@utils/iconUtils";
import { isIosNativeApp, isNativeApp } from "@utils/platformUtils";

const PushNotificationSetup = () => {
  const me = useMe();
  const registered = useRef<boolean>(false);

  const [present, dismiss] = useIonToast();

  const [createPushNotificationToken] = useMutationWithValidation<
    PushNotificationTokenCreateMutation,
    PushNotificationTokenCreateMutationVariables
  >(PushNotificationTokenCreateDocument, "pushNotificationToken");

  const platform = useMemo(() => {
    if (isIosNativeApp()) {
      return PushNotificationPlatformEnum.IOS;
    }
    return PushNotificationPlatformEnum.ANDROID;
  }, []);

  // const executeAction = useCallback(
  //   (data: any) => {
  //     apolloClient.clearStore().then(() => {
  //       const action = data.action;
  //       const journeyId = data.journeyId;
  //       const userType = data.userType;
  //       switch (action) {
  //         case PushNotificationActionEnum.OPEN_CARE_PLAN:
  //           history.push(
  //             journeyRoute({
  //               journeyId,
  //               linkAfterJourney: '/care-plan',
  //               myClient: userType === 'Client',
  //               myExpert: userType === 'Expert'
  //             })
  //           );
  //           break;
  //         case PushNotificationActionEnum.OPEN_CONSULTS:
  //           history.push(
  //             journeyRoute({
  //               journeyId,
  //               linkAfterJourney: '/consults',
  //               myClient: userType === 'Client',
  //               myExpert: userType === 'Expert'
  //             })
  //           );
  //           break;
  //         case PushNotificationActionEnum.OPEN_MESSAGES:
  //           messagingOpener.open();
  //           break;
  //       }
  //     });
  //   },
  //   [apolloClient, history, messagingOpener]
  // );

  useEffect(() => {
    if (isNativeApp() && me) {
      if (!registered.current) {
        registered.current = true;

        PushNotifications.addListener("registration", registrationData => {
          createPushNotificationToken({
            platform,
            token: registrationData.value
          });
        });

        const registerForNotifications = async () => {
          let permStatus = await PushNotifications.checkPermissions();

          if (permStatus.receive === "prompt") {
            permStatus = await PushNotifications.requestPermissions();
          }

          if (permStatus.receive === "granted") {
            await PushNotifications.register();
          }
        };

        registerForNotifications();

        PushNotifications.addListener("registrationError", error => {
          // eslint-disable-next-line no-console
          console.log("+++++ Error on push notification registration: " + JSON.stringify(error));
        });

        PushNotifications.addListener("pushNotificationReceived", (notification: PushNotificationSchema) => {
          // The app is open and we got a notification. The system one will not be displayed, so we need to make our own.
          // Show a toast!
          present({
            buttons: [{ handler: () => dismiss(), icon: IconList.xmark }],
            cssClass: "pushNotificationToast",
            duration: 30_000,
            message: notification.body,
            position: "top"
          });
        });

        PushNotifications.addListener("pushNotificationActionPerformed", (notification: ActionPerformed) => {
          // eslint-disable-next-line no-console
          console.log("++++++ Received a push notification action performed " + JSON.stringify(notification));
          // Method called when tapping on a notification when the app is not open. Perform the action requested in the
          // push data. We have some work to do with trialing push notificiations in order to know what data will be
          // received and how this would work.
          // executeAction(notification.notification.data);
        });
      }

      return () => {
        PushNotifications.removeAllListeners();
      };
    }
  }, [createPushNotificationToken, dismiss, me, platform, present]);

  return null;
};

export default PushNotificationSetup;
